import * as React from "react"
import { Modal, Form, Button, Col, InputGroup, Spinner } from "react-bootstrap"
import { Subscription, Subject, of, concat } from "rxjs"
import { switchMap, filter, debounceTime, map, catchError } from "rxjs/operators"
import { toast } from "react-toastify"
import LabAccount from "../../api/Randoms/RandomsModel/LabAccount"
import LabAccountListGetObservable from "../../api/Randoms/LabAccountsGet"
import LabPanel from "../../api/Randoms/RandomsModel/LabPanel"
import LabPanelsGetObservable from "../../api/Randoms/LabPanelsGet"
import RegulatoryMode from "../../api/LabService/LabServiceModel/RegulatoryMode"
import SelectionIntervalsGetObservable from "../../api/Randoms/SelectionIntervalsGet"
import LabServiceRegulatoryModesGetObservable from "../../api/LabService/LabServiceRegulatoryModesGet"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterCompaniesGet from "../../api/Companies/AutoFilterCompaniesGet"
import CompanyInput from "../FormattedInputs/CompanyInput"
import { PoolCreateObservable } from "../../api/Randoms/PoolSave"
import PoolCreate from "../../api/Randoms/RandomsModel/PoolCreate"
import { isCompanyAdmin, isPortalAdmin } from "../../authentication/isAdminRole"
import { AppState, AssignedClinic } from "../../AppState"
import { connect } from "react-redux"
import PrimaryIDType from "../../api/PrimaryIDTypes/PrimaryIDType"
import { PoolPrimaryIDTypesGetObservable } from "../../api/PrimaryIDTypes/PrimaryIDTypesGet"
import { IsOnTrack } from "../../App";
import LoadingIndicatorWrapper from "../LoadingIndicatorWrapper"
import { GetSelectedAssignedClinic, SelectedPortalName } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import Checkbox from "../Checkbox"
import { Typeahead } from "react-bootstrap-typeahead"

interface AddPoolModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
  IsConsortium: boolean
}

interface AddPoolModalAuthProps {
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
  currentCompany?: AssignedClinic | null
}

interface AddPoolModalState {
  companies: AutoFilterCompany[]
  initialCompanies?: AutoFilterCompany[]
  companiesLoading: boolean
  regulatoryModesLoading: boolean
  selectionIntervalsLoading: boolean
  labAccountLoading: boolean
  primaryIDTypesLoading: boolean
  panelsLoading: boolean
  selectedCompany?: AutoFilterCompany,
  regulatoryModes?: RegulatoryMode[]
  selectionIntervals?: string[]
  labAccounts?: LabAccount[]
  primaryIDTypes?: PrimaryIDType[]
  labPanels?: LabPanel[]
  poolName: string
  poolNameValid: boolean
  poolID: string
  federallyRegulated: boolean | string
  regulatoryModeId: number
  selectionInterval: string
  calcType: string
  drugPoolSize: string
  alcoholPoolSize: string
  labAccount: string
  panelID: string
  useFileImportForPoolMembership: string
  primaryIDType: string
  saving: boolean
  currentCompany?: AssignedClinic | null
  includeCompaniesWithNoSelections: boolean
  panelCode: string
  panelCodeValid: boolean
  selectedRegulatoryModes: RegulatoryMode[]
  availableToSelectRegModes: RegulatoryMode[]
  RunOnlyByPortalAdmin: boolean
}

export class AddPoolModal extends React.PureComponent<AddPoolModalProps & AddPoolModalAuthProps, AddPoolModalState> {
  state: AddPoolModalState = {
    companies: [],
    companiesLoading: false,
    regulatoryModesLoading: false,
    selectionIntervalsLoading: false,
    labAccountLoading: false,
    panelsLoading: false,
    primaryIDTypesLoading: false,
    poolName: "",
    poolNameValid: true,
    poolID: "",
    federallyRegulated: "",
    regulatoryModeId: 0,
    selectionInterval: "",
    calcType: "",
    drugPoolSize: "",
    alcoholPoolSize: "",
    labAccount: "",
    panelID: "",
    useFileImportForPoolMembership: this.props.IsConsortium ? "false" : "true",
    primaryIDType: "",
    saving: false,
    currentCompany: this.props.currentCompany,
    includeCompaniesWithNoSelections: false,
    panelCode: "",
    panelCodeValid: true,
    selectedRegulatoryModes: [],
    availableToSelectRegModes: [],
    RunOnlyByPortalAdmin: false || this.props.IsConsortium,
  }

  regulatoryModesSubscription?: Subscription
  intervalsSubscription?: Subscription
  labAccountsSubscription?: Subscription
  primaryIDTypesSubscription?: Subscription
  account$ = new Subject<LabAccount>()
  labAccountSubscription?: Subscription
  saveSubscription?: Subscription

  componentDidMount() {

    this.setState({ regulatoryModesLoading: true, selectionIntervalsLoading: true, primaryIDTypesLoading: true, labAccountLoading: true })

    this.regulatoryModesSubscription = LabServiceRegulatoryModesGetObservable(true).subscribe(
      (data: RegulatoryMode[]) => this.setState({ regulatoryModes: data, availableToSelectRegModes: data || [], regulatoryModesLoading: false }),
      () => this.setState({ regulatoryModesLoading: false })
    )

    this.intervalsSubscription = SelectionIntervalsGetObservable().subscribe(
      (data: string[]) => this.setState({ selectionIntervals: data, selectionIntervalsLoading: false }),
      () => this.setState({ selectionIntervalsLoading: false })
    )

    this.primaryIDTypesSubscription = PoolPrimaryIDTypesGetObservable().subscribe(
      (data: PrimaryIDType[]) => this.setState({ primaryIDTypes: data, primaryIDTypesLoading: false, primaryIDType: data?.length ? data[0].PrimaryIDType : "" }),
      () => this.setState({ primaryIDTypesLoading: false })
    )
    const link = sessionStorage.getItem("company-selection-link") || ""

    this.labAccountsSubscription = LabAccountListGetObservable(link, this.state.selectedCompany?.AccountID).subscribe(
      (data: LabAccount[]) =>{ 
        const filteredLabs = data.filter((labAccount: LabAccount) => (labAccount.LabCode === "CRL"))
        this.setState({ labAccounts: filteredLabs, labAccountLoading: false })
      },
      () => this.setState({ labAccountLoading: false })
    )

    this.labAccountSubscription = this.account$.pipe(
      filter(labAccount => labAccount !== undefined),
      switchMap(labAccount =>
        concat(
          of({ loading: true }),
          LabPanelsGetObservable(link, labAccount.LabAccount, labAccount.LabClientMemberId).pipe(
            map(data => ({ loading: false, data: data })),
            catchError(() => of({ loading: false }))
          )
        )
      )
    ).subscribe(
      (o: { loading: boolean, data?: LabPanel[] }) =>
        o.data
          ? this.setState({ panelsLoading: o.loading, labPanels: o.data })
          : this.setState({ panelsLoading: o.loading }),
      () => this.setState({ panelsLoading: false })
    )
  }

  componentDidUpdate(prevProps: AddPoolModalProps) {
    if (!prevProps.show && this.props.show) {
      this.setState({
        selectedCompany: undefined,
        poolName: "",
        poolNameValid: true,
        poolID: "",
        federallyRegulated: "",
        regulatoryModeId: 0,
        selectionInterval: "",
        calcType: "",
        drugPoolSize: "",
        alcoholPoolSize: "",
        labAccount: "",
        panelID: "",
        useFileImportForPoolMembership: "true",
        primaryIDType: "",
        saving: false,
        selectedRegulatoryModes: []
      })
    }
  }

  componentWillUnmount() {
    this.regulatoryModesSubscription && this.regulatoryModesSubscription.unsubscribe()
    this.intervalsSubscription && this.intervalsSubscription.unsubscribe()
    this.labAccountsSubscription && this.labAccountsSubscription.unsubscribe()
    this.primaryIDTypesSubscription && this.primaryIDTypesSubscription.unsubscribe()
    this.labAccountSubscription && this.labAccountSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  checkPoolSize = (value: string) => {
    if (this.state.calcType === "Rate" && /^(\s*|100|0|[1-9]{1}\d?)$/.test(value)) {
      return true
    }
    if (this.state.calcType === "Fixed" && /^(\s*|0|[1-9]{1}\d{0,15})$/.test(value)) {
      return true
    }
    return false
  }

  handleCompanyChange = (items: AutoFilterCompany[]) => {
    const selectedCompany = items[0];
    this.setState({ selectedCompany });

    const link = sessionStorage.getItem("company-selection-link") || ""
    
    this.labAccountsSubscription && this.labAccountsSubscription.unsubscribe()
    this.setState({ labAccountLoading: true})
    this.labAccountsSubscription = LabAccountListGetObservable(link, selectedCompany?.AccountID).subscribe(
      (data: LabAccount[]) =>{ 
        const filteredLabs = data.filter((labAccount: LabAccount) => (labAccount.LabCode === "CRL"))
        this.setState({ labAccounts: filteredLabs, labAccountLoading: false })
      },
      () => this.setState({ labAccountLoading: false })
    )
  }

  handlePoolNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.setState({ poolName: value, poolNameValid: value.length >= 8 })
  }

  handlePanelCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.setState({ panelCode: value, panelCodeValid: value.length > 1 })
  }

  handlePoolIDChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.setState({ poolID: value })
  }


  handleFederallyRegulatedChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ federallyRegulated: event.target.value, panelID: "", regulatoryModeId: 0, selectedRegulatoryModes: [], availableToSelectRegModes: this.state.regulatoryModes || [] })
  }

  handleIntervalChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectionInterval = event.target.value
    this.setState({ selectionInterval: selectionInterval })
  }

  handleCalcTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const calcType = event.target.value
    this.setState({ calcType: calcType, drugPoolSize: "", alcoholPoolSize: "" })
  }

  handleDrugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const drug = event.target.value
    if (this.checkPoolSize(drug)) {
      this.setState({ drugPoolSize: drug })
    }
  }

  handleAlcoholChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const alcohol = event.target.value
    if (this.checkPoolSize(alcohol)) {
      this.setState({ alcoholPoolSize: alcohol })
    }
  }

  handleLabChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const labAccount = event.target.value
    this.setState({ labAccount: labAccount, labPanels: undefined, panelID: "" })
    const currentAccount = this.state.labAccounts?.find((account: LabAccount) => account.LabAccount === labAccount)
    this.account$.next(currentAccount)
  }

  handlePanelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const panelID = event.target.value
    this.setState({ panelID: panelID })
  }

  handlePoolMembershipTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const useFileImportForPoolMembership = event.target.value
    this.setState({ useFileImportForPoolMembership: useFileImportForPoolMembership })
  }
  handlePrimaryIDTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value || ""
    this.setState({ primaryIDType: value })
  }

  isValid = () => {
    return (this.props.IsConsortium || this.state.selectedCompany || this.state.currentCompany)
      && this.state.poolName
      && this.state.poolNameValid
      && this.state.poolID
      && (this.state.federallyRegulated === "false" || (this.state.federallyRegulated === "true" && this.state.regulatoryModeId !== 0 && this.state.selectedRegulatoryModes.length >= 0))
      && this.state.selectionInterval
      && this.state.calcType
      && this.state.drugPoolSize
      && this.state.alcoholPoolSize
      && (this.props.IsConsortium || this.state.labAccount)
      && (this.props.IsConsortium || this.state.panelID)
      && this.state.useFileImportForPoolMembership !== ""
      && this.state.primaryIDType !== ""
      && (this.props.IsConsortium ? this.state.panelCode && this.state.panelCodeValid : true)
  }

  performSave = () => {
    const pool: PoolCreate = {
      IsNonDOT: this.state.federallyRegulated === "false" || !this.state.federallyRegulated,
      AccountId: this.state.selectedCompany?.AccountID || this.state.currentCompany?.AccountID || 0,
      PoolName: this.state.poolName,
      PoolCode: this.state.poolID,
      RegulatoryModeID: this.state.regulatoryModeId !== 0 ? this.state.regulatoryModeId : null,
      SelectionInterval: this.state.selectionInterval,
      DrugTarget: Number(this.state.drugPoolSize),
      AlcoholTarget: Number(this.state.alcoholPoolSize),
      PoolCalculationType: this.state.calcType,
      ProviderMappingID: this.state.labAccounts ? this.state.labAccounts.find((account: LabAccount) => account.LabAccount === this.state.labAccount)?.ProviderMappingID || 0 : 0,
      PanelID: this.state.panelID ? Number(this.state.panelID) : 0,
      UseFileImportForPoolMembership: this.state.useFileImportForPoolMembership,
      PrimaryIDType: this.state.primaryIDType,
      IncludeCompaniesWithNoSelections: this.state.includeCompaniesWithNoSelections,
      IsConsortium: this.props.IsConsortium,
      PanelCode: this.props.IsConsortium ? this.state.panelCode : "",
      RegulatoryModes: this.state.selectedRegulatoryModes.map(({ RegulatoryModeID }) => RegulatoryModeID),
      RunOnlyByPortalAdmin: this.state.RunOnlyByPortalAdmin || this.props.IsConsortium
    }

    this.setState({ saving: true })

    this.saveSubscription = PoolCreateObservable(pool).subscribe(
      data => {
        toast.success(data)
        this.setState({ saving: false })
        this.props.onSuccess()
      },
      error => {
        this.setState({ saving: false })
        toast.error(error?.errorData?.message || error.message)
      }
    )
  }

  onRegulatoryModeChange = (selected: RegulatoryMode[]) => {
    const availableToSelectRegModes = []
    const selectedRegModes = selected || []
    const availableRegModes = this.state.regulatoryModes || []
    let regulatoryModeId = 0
    if (selectedRegModes.length > 0) {
      regulatoryModeId = selectedRegModes[0].RegulatoryModeID
    }

    for (let i = 0; i < availableRegModes.length; i++) {
      let is_selected = false;
      const availRegMode = availableRegModes[i]
      for (let j = 0; j < selectedRegModes.length; j++) {
        const selectedRegMode = selectedRegModes[j];
        if (availRegMode.RegulatoryModeID === selectedRegMode.RegulatoryModeID) {
          is_selected = true
          break
        }
      }
      if (is_selected) {
        continue
      }
      availableToSelectRegModes.push(availRegMode)
    }
    this.setState({ regulatoryModeId: regulatoryModeId, selectedRegulatoryModes: selectedRegModes, availableToSelectRegModes: availableToSelectRegModes })
  }

  handleRunOnlyByCompanyAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if(!this.state.RunOnlyByPortalAdmin){
      return
    }
    const checked = event.target.checked
    if(!checked){
      return
    }
    this.setState({ RunOnlyByPortalAdmin: !checked })

  }

  handleRunOnlyByPortalAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if(this.state.RunOnlyByPortalAdmin){
      return
    }
    const checked = event.target.checked
    if(!checked){
      return
    }
    this.setState({ RunOnlyByPortalAdmin: checked })
  }

  render() {
    const { show, onHide, IsConsortium } = this.props
    const isOnTrack = IsOnTrack()
    const availableRegulatoryModes = this.state.regulatoryModes || []
    const availableToSelectRegModes = this.state.availableToSelectRegModes
    const selectedRegulatoryModes = this.state.selectedRegulatoryModes
    const regulatoryModeId = this.state.regulatoryModeId
    const regulatoryModeDisabled = (this.state.federallyRegulated === "") || (this.state.federallyRegulated === "false") || (availableRegulatoryModes.length == 0)
    const runOnlyByPortalAdmin = this.state.RunOnlyByPortalAdmin
    const isPortalAdmin: boolean = this.props.isPortalAdmin || false

    let calcTypeMark = ""
    switch (this.state.calcType) {
      case "Rate":
        calcTypeMark = "%"
        break
      case "Fixed":
        calcTypeMark = "#"
        break
    }
    let primaryIdType = "SSN"
    if ((this.state.primaryIDTypes) && (this.state.primaryIDTypes[0])) {
      primaryIdType = this.state.primaryIDTypes[0].PrimaryIDType
    }
    if (this.state.selectedCompany?.PrimaryIDType) {
      primaryIdType = this.state.selectedCompany?.PrimaryIDType
    }
    if (this.state.primaryIDType) {
      primaryIdType = this.state.primaryIDType
    }

    const companyName = this.state.selectedCompany?.Name || this.state.currentCompany?.Name || ""
    return <Modal backdrop="static" centered show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{IsConsortium ? "Create New Consortium" : "Create New Pool"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {!IsConsortium && <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="company">Company</Form.Label>
            {this.props.isCompanyAdmin && <Col sm={9}>
              {this.state.companiesLoading && <div className="py-2"><Spinner animation="border" size="sm" variant="secondary" /></div>}
              {!this.state.companiesLoading && <Form.Control type="text" disabled={true} className="bg-white" defaultValue={companyName} />}
            </Col>}
            {this.props.isPortalAdmin && <Col sm={9}>
              <CompanyInput onChange={this.handleCompanyChange} placeholder="Choose a company..." />
            </Col>}
          </Form.Row>}
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="poolName">Pool Name</Form.Label>
            <Col sm={9}>
              <Form.Control id="poolName" type="text" maxLength={60} onChange={this.handlePoolNameChange} isInvalid={!this.state.poolNameValid} />
              <Form.Control.Feedback type="invalid">Pool Name should contain at least 8 characters</Form.Control.Feedback>
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="poolID">Pool ID</Form.Label>
            <Col sm={9}>
              <Form.Control id="poolID" type="text" maxLength={10} onChange={this.handlePoolIDChange} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="federallyRegulated">Federally Regulated</Form.Label>
            <Col sm={9}>
              <Form.Control id="federallyRegulated" as="select" onChange={this.handleFederallyRegulatedChange}>
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Control>
            </Col>
          </Form.Row>
          {this.state.federallyRegulated === "true" && <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="regulatoryMode">Regulatory Mode(s):</Form.Label>
            <Col sm={9}>
              <LoadingIndicatorWrapper loading={this.state.regulatoryModesLoading}>
                <Typeahead id="regulatoryModeTypeahead"
                  labelKey="Name"
                  filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                  onChange={(selected) => { this.onRegulatoryModeChange(selected) }}
                  options={availableToSelectRegModes}
                  isInvalid={selectedRegulatoryModes.length === 0}
                  disabled={regulatoryModeDisabled}
                  emptyLabel="No Regulatory Modes Found"
                  placeholder="Select"
                  multiple
                  selected={selectedRegulatoryModes ? selectedRegulatoryModes : []}
                />
              </LoadingIndicatorWrapper>
            </Col>
          </Form.Row>}
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="selectionInterval">Selection Interval</Form.Label>
            <Col sm={9}>
              <LoadingIndicatorWrapper loading={this.state.selectionIntervalsLoading}>
                <Form.Control id="selectionInterval" as="select" onChange={this.handleIntervalChange}>
                  <option value="">Select</option>
                  {this.state.selectionIntervals && this.state.selectionIntervals.map((interval: string) => <option value={interval} key={interval}>{interval}</option>)}
                </Form.Control>
              </LoadingIndicatorWrapper>
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="poolCalcType">Pool Calculation Type</Form.Label>
            <Col sm={9}>
              <Form.Control id="poolCalcType" as="select" onChange={this.handleCalcTypeChange}>
                <option value="">Select</option>
                <option value="Rate">Annual Percentage</option>
                <option value="Fixed">Fixed Number of Selections for Program Year</option>
              </Form.Control>
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="drug">Drug</Form.Label>
            <Col sm={9}>
              <InputGroup>
                <Form.Control id="drug" type="text" value={this.state.drugPoolSize} onChange={this.handleDrugChange} />
                <InputGroup.Append hidden={!calcTypeMark}><InputGroup.Text>{calcTypeMark}</InputGroup.Text></InputGroup.Append>
              </InputGroup>
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="alcohol">Alcohol</Form.Label>
            <Col sm={9}>
              <InputGroup>
                <Form.Control id="alcohol" type="text" value={this.state.alcoholPoolSize} onChange={this.handleAlcoholChange} />
                <InputGroup.Append hidden={!calcTypeMark}><InputGroup.Text>{calcTypeMark}</InputGroup.Text></InputGroup.Append>
              </InputGroup>
            </Col>
          </Form.Row>
          {!IsConsortium &&
            <Form.Row className="form-group required">
              <Form.Label column sm={3} htmlFor="labAccount">Select your Lab</Form.Label>
              <Col sm={9}>
                <LoadingIndicatorWrapper loading={this.state.labAccountLoading}>
                  <Form.Control id="labAccount" as="select" onChange={this.handleLabChange}>
                    <option value="">Select</option>
                    {this.state.labAccounts && this.state.labAccounts.map((labAccount: LabAccount) => <option value={labAccount.LabAccount} key={labAccount.LabAccount}>{labAccount.Description}</option>)}
                  </Form.Control>
                </LoadingIndicatorWrapper>
              </Col>
            </Form.Row>}
          {!IsConsortium &&
            <Form.Row className="form-group required">
              <Form.Label column sm={3} htmlFor="panel">Panel</Form.Label>
              <Col sm={9}>
                <LoadingIndicatorWrapper loading={this.state.panelsLoading}>
                  <Form.Control id="panel" as="select" onChange={this.handlePanelChange} value={this.state.panelID}>
                    <option value="">Select</option>
                    {this.state.labPanels && this.state.labPanels
                      .filter(((labPanel: LabPanel) => (labPanel.DOT && this.state.federallyRegulated === "true") || (!labPanel.DOT && this.state.federallyRegulated === "false")))
                      .map((labPanel: LabPanel) => <option value={labPanel.PanelID} key={labPanel.PanelID}>{labPanel.Description}</option>)}
                  </Form.Control>
                </LoadingIndicatorWrapper>
              </Col>
            </Form.Row>}
          {IsConsortium && <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="poolPanelCode">Panel Code</Form.Label>
            <Col sm={9}>
              <Form.Control id="poolPanelCode" type="text" maxLength={60} onChange={this.handlePanelCodeChange} isInvalid={!this.state.panelCodeValid} />
              <Form.Control.Feedback type="invalid">Panel Code should contain at least 2 characters</Form.Control.Feedback>
            </Col>
          </Form.Row>}
          <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="poolMembershipType">Pool Membership Type</Form.Label>
            <Col sm={9}>
              <Form.Control id="poolMembershipType" as="select" value={this.state.useFileImportForPoolMembership} onChange={this.handlePoolMembershipTypeChange}>
                {<option value="">Select</option>}
                {<option value="true">File Import</option>}
                <option value="false">Criteria</option>
              </Form.Control>
            </Col>
          </Form.Row>
          {IsConsortium &&
            <Form.Row>
              <Form.Label column sm={3} htmlFor="includeNoSelection">Include companies with no selection</Form.Label>
              <Col sm={9}><Checkbox id="includeNoSelection" name="IncludeNoSelection" label="" checked={this.state.includeCompaniesWithNoSelections} className="pt-2 ml-3" onChange={(e) => this.setState({ includeCompaniesWithNoSelections: e.target.checked })} /></Col>
            </Form.Row>}
          {!isOnTrack && <Form.Row className="form-group required">
            <Form.Label column sm={3} htmlFor="primaryIDType">Pool Report ID Type</Form.Label>
            <Col sm={9}>
              <LoadingIndicatorWrapper loading={this.state.primaryIDTypesLoading}>
                <Form.Control id="primaryIDType" as="select" value={primaryIdType} onChange={this.handlePrimaryIDTypeChange}>
                  {this.state.primaryIDTypes?.map(item => <option value={item.PrimaryIDType} key={item.PrimaryIDType}>{item.PrimaryIDType}</option>)}
                </Form.Control>
              </LoadingIndicatorWrapper>
            </Col>
          </Form.Row>}
          {!isOnTrack && !IsConsortium && <Form.Row>
            <Form.Label column xs={6} md={4}>WHO CAN RUN POOL</Form.Label>
            <Col>
              <Checkbox id="runOnlyByCompanyAdmin" name="RunOnlyByCompanyAdmin" label="Company Administrator Only" checked={!runOnlyByPortalAdmin}  disabled={!isPortalAdmin} className="pt-2 ml-3" onChange={this.handleRunOnlyByCompanyAdminChange} />
            </Col>
            <Col>
              <Checkbox id="runOnlyByPortalAdmin" name="RunOnlyByPortalAdmin" label="Portal Administrator Only" checked={runOnlyByPortalAdmin}  disabled={!isPortalAdmin} className="pt-2 ml-3" onChange={this.handleRunOnlyByPortalAdminChange} />
            </Col>
          </Form.Row>}

        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>CANCEL</Button>
        <Button variant="primary" onClick={this.performSave} disabled={!this.isValid()}>SUBMIT</Button>
      </Modal.Footer>
    </Modal>
  }
}

function mapStateToProps(state: AppState): AddPoolModalAuthProps {
  const companyAdmin = isCompanyAdmin(state)
  const portalAdmin = isPortalAdmin(state)
  const currentCompany = companyAdmin ? GetSelectedAssignedClinic(state) : undefined
  return {
    isCompanyAdmin: companyAdmin,
    isPortalAdmin: portalAdmin,
    currentCompany: currentCompany
  }
}
const ConnectedAddPoolModal = connect(mapStateToProps, null)(AddPoolModal)
export default ConnectedAddPoolModal